import { useMemo } from 'react';
import { Modality } from '~/domain/dicom/modality';
import usePrimaryImage, { AvailableImage } from '~/hooks/image/usePrimaryImage';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import AppConfig from '~/utils/appconfig';

export interface CompositeCbctEligibility {
    readonly canBuild: boolean;
    readonly blockedMessageId?: string;
}

export function isCompositeCbctEligible(image: AvailableImage): boolean {
    // Keep CT images that are either already loaded into an Image3D
    // or advertised as session-only (isLoaded: false, imageObject
    // absent). Session-only entries are acceptable in connector mode
    // because the connector already holds the DICOM — the task hook
    // submits by UID. Direct-backend mode never surfaces session-
    // only images (no session route), so narrowing there is handled
    // inside the task hook. `needsReformat` series have no usable
    // voxel grid for the AI backend and are always excluded.
    if (image.modality !== Modality.CT) {
        return false;
    }
    if (image.needsReformat) {
        return false;
    }
    if (image.imageObject) {
        return true;
    }
    // Session-only entries are only actionable in connector mode;
    // hide them in direct-backend mode so the user can't pick
    // something the backend path can't consume.
    return AppConfig.isConnectorMode && image.isLoaded === false;
}

export function useCompositeCbctEligibility(): CompositeCbctEligibility {
    const { availableImages } = usePrimaryImage(true);
    const selectedPatient = useSelectedPatient();
    const primaryImage = useSelectedPrimaryImage();

    const eligibleImageCount = useMemo(
        () => availableImages.filter(isCompositeCbctEligible).length,
        [availableImages]
    );

    const blockedMessageId =
        !selectedPatient || !primaryImage
            ? 'loadImageFirst'
            : eligibleImageCount < 2
              ? 'compositeCbctEligibleImagesRequired'
              : undefined;

    return {
        canBuild: !blockedMessageId,
        blockedMessageId,
    };
}
